import { inject } from '@angular/core';
import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { Router } from '@angular/router';
import { catchError, switchMap, throwError } from 'rxjs';
import { AuthService } from '../services/auth.service';
import { NotificationService } from '../services/notification.service';
import { environment } from '../../environments/environment';
import { BUSINESS_CONTEXT_HEADER } from '../utils/business-context';

// Cualquier 401 en una petición que sí llevaba sesión significa "tu sesión
// ya no es válida" (expiró, fue invalidada, o el usuario fue desactivado).
// Se maneja UNA sola vez, centralizado aquí, en vez de que cada componente
// decida por su cuenta si muestra el texto crudo que mande el backend.
let handlingSessionExpiry = false;

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const auth = inject(AuthService);
  const notifications = inject(NotificationService);
  const router = inject(Router);
  const token = auth.getToken();

  const isSessionEndpoint = req.url.includes('/saas/owner-login') || req.url.includes('/saas/session/refresh') || req.url.includes('/saas/session/logout') || req.url.includes('/saas/session/extend');
  // "Gestionar negocio" (modal de Super Admin): cada petición a la API indica
  // el negocio que se está operando. Nunca se envía a servicios de terceros.
  const contextHeaders: Record<string, string> = auth.businessContextId && req.url.startsWith(environment.apiUrl)
    ? { [BUSINESS_CONTEXT_HEADER]: String(auth.businessContextId) }
    : {};
  const withContext = (base: typeof req) => Object.keys(contextHeaders).length ? base.clone({ setHeaders: contextHeaders }) : base;
  const request = withContext(token && !isSessionEndpoint
    ? req.clone({ setHeaders: { Authorization: token.startsWith('Bearer ') ? token : `Bearer ${token}` } })
    : req);

  return next(request).pipe(
    catchError((error: unknown) => {
      // Una cuenta exenta (super_admin, o propietario delegado/empleado de un
      // negocio cuyo dueño está exento) nunca debe ver la superposición de
      // bloqueo por suscripción, aunque algún endpoint responda por error con
      // uno de estos códigos -- el error se sigue propagando al componente.
      const subscriptionExempt = !!auth.currentUser()?.subscription_exempt;
      if (!subscriptionExempt && error instanceof HttpErrorResponse && error.status === 403 && error.error?.code === 'TRIAL_EXPIRED') {
        auth.trialExpired.set(true);
      }
      // Función no incluida en el plan del negocio (requirePlanFeature): el
      // propietario recibe un acceso directo a sus planes; un empleado, la
      // indicación de pedírselo al propietario. El mensaje del error se
      // reemplaza por el mismo texto del aviso para que el aviso propio de
      // cada pantalla (mismo texto y tipo) se descarte como duplicado.
      if (!subscriptionExempt && error instanceof HttpErrorResponse && error.status === 403 && error.error?.code === 'PLAN_FEATURE_RESTRICTED') {
        const isOwner = auth.currentUser()?.role === 'owner';
        const message = isOwner
          ? 'Esta función no está incluida en tu plan actual. Mejora tu plan para usarla.'
          : 'Esta función no está incluida en el plan del negocio. Pídele al propietario que mejore el plan.';
        if (error.error && typeof error.error === 'object') error.error.msg = message;
        notifications.show(message, 'error', 9000, undefined, isOwner
          ? { label: 'Ver planes', run: () => router.navigate(['/dashboard'], { queryParams: { module: 'suscripcion' } }) }
          : undefined);
      }
      if (!subscriptionExempt && error instanceof HttpErrorResponse && error.status === 403 && error.error?.code === 'SUBSCRIPTION_RESTRICTED') {
        auth.subscriptionRestricted.set(true);
      }
      if (error instanceof HttpErrorResponse && error.status === 401 && request.headers.has('Authorization') && !isSessionEndpoint) {
        return auth.refreshSession().pipe(switchMap(refreshed => {
          const refreshedToken = auth.getToken();
          if (refreshed && refreshedToken) {
            return next(withContext(req.clone({ setHeaders: { Authorization: `Bearer ${refreshedToken}` } })));
          }
          // Dentro del modal la ventana del super admin ya fue avisada (y lo cierra).
          if (auth.isBusinessContextMode()) return throwError(() => error);
          if (!auth.wasLastRefreshTerminal()) {
            if (!handlingSessionExpiry) {
              handlingSessionExpiry = true;
              notifications.show('No pudimos renovar tu sesión en este momento. Intenta nuevamente.', 'info', 6000);
              setTimeout(() => { handlingSessionExpiry = false; }, 2000);
            }
            return throwError(() => error);
          }
          if (!handlingSessionExpiry) {
            handlingSessionExpiry = true;
            notifications.show('Tu sesión expiró. Inicia sesión nuevamente.', 'info', 6000);
            router.navigate(['/login']);
            setTimeout(() => { handlingSessionExpiry = false; }, 2000);
          }
          return throwError(() => error);
        }));
      }
      return throwError(() => error);
    })
  );
};
